import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';
import { useToastStore, type ToastType } from '@/stores/toast.store';
import { appMainFixedHorizontalInsetClass, useAppShellMainInset } from '@/lib/app-shell-layout';
import { useUIStore } from '@/stores/ui.store';
import { cn } from '@/lib/cn';

const ICON_MAP: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const STYLE_MAP: Record<ToastType, string> = {
  success: 'border-teal-200 bg-teal-50 text-teal-800',
  error: 'border-red-200 bg-red-50 text-red-800',
  info: 'border-gray-200 bg-gray-50 text-gray-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-800',
};

const ICON_STYLE_MAP: Record<ToastType, string> = {
  success: 'text-teal-600',
  error: 'text-red-600',
  info: 'text-gray-600',
  warning: 'text-amber-600',
};

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const useMainInset = useAppShellMainInset();

  if (toasts.length === 0) return null;

  return (
    <div
      className={cn(
        'pointer-events-none fixed bottom-24 z-toast flex flex-col items-center gap-2 px-4',
        useMainInset
          ? cn('right-0', appMainFixedHorizontalInsetClass(sidebarCollapsed))
          : 'inset-x-0',
        'pb-[max(0px,env(safe-area-inset-bottom))]',
      )}
    >
      {toasts.map((t) => {
        const Icon = ICON_MAP[t.type];
        return (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-md border px-4 py-3 shadow-lg',
              'animate-in fade-in slide-in-from-bottom-4 duration-200',
              STYLE_MAP[t.type],
            )}
          >
            <Icon className={cn('h-5 w-5 shrink-0', ICON_STYLE_MAP[t.type])} />
            <p className="text-sm font-medium">{t.message}</p>
            <button
              type="button"
              onClick={() => removeToast(t.id)}
              className="ml-auto shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
